import type {PayoutCashBridge} from "@workspace/api-client-react";
import { useState, useEffect } from "react";
import { LoaderCircle, CheckCircle2, ChevronDown, ChevronUp, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function pence(p: number) { return `£${(Math.abs(p) / 100).toFixed(2)}`; }

type Store = { id: number; name: string; stripeAccountId: string | null; stripeOnboardingComplete: boolean | null; ownerName: string | null; ownerEmail: string | null };
type StripeBal = { available: { amount: number; currency: string }[]; pending: { amount: number; currency: string }[] };
type StripePayout = { id: string; amount: number; currency: string; arrival_date: number; status: string; description: string | null };
type Overview = { balance: StripeBal; payouts: StripePayout[] };

type RptOrder = { id: number; customerName: string | null; subtotalPence: number; bagChargePence: number | null; deliveryFeePence: number; leg2DeliveryFeePence: number | null; customerPlatformFeePence: number; customerHubDriverFeePence: number; driverFuelSupplementPence: number | null; createdAt: string; status: string };
type RptCharge = { chargeId: string; paymentIntentId: string | null; grossPence: number; stripeProcessingFeePence: number; applicationFeePence: number; deliveryFeePence: number; platformFeePence: number; hubDriverFeePence: number; fuelSupplementPence: number; netToStorePence: number; order: RptOrder | null };
type RptPayout = { id: string; arrivalDate: string; createdDate: string; status: string; amountPence: number; description: string | null; charges: RptCharge[]; totalGrossPence: number; totalStripeProcessingFeePence: number; totalApplicationFeePence: number; totalDeliveryFeePence: number; totalPlatformFeePence: number; totalHubDriverFeePence: number; totalFuelSupplementPence: number; totalNetToStorePence: number; unmatchedCount: number; bridge?: PayoutCashBridge };
type RptWeek = { periodStart: string; periodEnd: string; orders: RptOrder[]; totalFoodRevenuePence: number; totalDeliveryFeePence: number; totalPlatformFeePence: number };
type RptData = { store: { id: number; name: string; stripeAccountId: string }; fromDate: string; toDate: string; payouts: RptPayout[]; weeks: RptWeek[]; grandTotalGrossPence: number; grandTotalStripeProcessingFeePence: number; grandTotalApplicationFeePence: number; grandTotalDeliveryFeePence: number; grandTotalPlatformFeePence: number; grandTotalHubDriverFeePence: number; grandTotalFuelSupplementPence: number; grandTotalPayoutsPence: number; grandPeriodRevenuePence: number };
type SinglePayout = { totalApplicationFeePence: number; payoutId: string; amountPence: number; arrivalDate: number; status: string; charges: RptCharge[]; totalGrossPence: number; totalStripeProcessingFeePence: number; totalDeliveryFeePence: number; totalPlatformFeePence: number; totalHubDriverFeePence: number; totalFuelSupplementPence: number; totalNetToStorePence: number; unmatchedCount: number };

export default function AdminReconciliation() {
  const { toast } = useToast();

  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Expandable recent payouts (overview section)
  const [expandedRecentPayoutId, setExpandedRecentPayoutId] = useState<string | null>(null);
  const [recentPayoutDetails, setRecentPayoutDetails] = useState<Map<string, SinglePayout>>(new Map());
  const [recentPayoutLoading, setRecentPayoutLoading] = useState<string | null>(null);
  const [payoutPage, setPayoutPage] = useState(0);
  const PAYOUT_PAGE_SIZE = 10;

  const [fromDate, setFromDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportData, setReportData] = useState<RptData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [expandedPayoutId, setExpandedPayoutId] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/admin/stripe/store-status", { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : [])
      .then((d: Store[]) => setStores(d))
      .catch(() => {})
      .finally(() => setStoresLoading(false));
  }, []);

  const selectedStore = stores.find((s) => s.id === selectedStoreId) ?? null;
  const connectedStores = stores.filter((s) => s.stripeAccountId && s.stripeOnboardingComplete);

  useEffect(() => {
    if (!selectedStoreId) { setOverview(null); return; }
    setOverviewLoading(true);
    setOverview(null);
    setReportData(null);
    setExpandedRecentPayoutId(null);
    setRecentPayoutDetails(new Map());
    setPayoutPage(0);
    fetch(`/api/admin/stores/${selectedStoreId}/stripe/overview`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d ? setOverview(d as Overview) : null)
      .catch(() => {})
      .finally(() => setOverviewLoading(false));
  }, [selectedStoreId]);

  async function loadRecentPayoutDetail(payoutId: string) {
    if (!selectedStoreId || recentPayoutDetails.has(payoutId)) return;
    setRecentPayoutLoading(payoutId);
    try {
      const res = await fetch(`/api/admin/stores/${selectedStoreId}/stripe/payout/${payoutId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json() as SinglePayout;
      setRecentPayoutDetails((m) => { const n = new Map(m); n.set(payoutId, data); return n; });
    } catch {
      // silently leave in loading state — user can retry by collapsing and re-expanding
    } finally {
      setRecentPayoutLoading(null);
    }
  }

  async function loadReport() {
    if (!selectedStoreId || !fromDate || !toDate) return;
    setReportLoading(true);
    setReportData(null);
    setExpandedPayoutId(null);
    try {
      const res = await fetch(`/api/admin/stores/${selectedStoreId}/stripe/reporting?from=${fromDate}&to=${toDate}`, { headers: authHeaders() });
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? `Error ${res.status}`); }
      setReportData(await res.json() as RptData);
    } catch (e: unknown) {
      toast({ title: (e instanceof Error ? e.message : null) ?? "Failed to load report", variant: "destructive" });
    } finally {
      setReportLoading(false);
    }
  }

  async function downloadPdf() {
    if (!selectedStoreId || !fromDate || !toDate || !selectedStore) return;
    setPdfDownloading(true);
    try {
      const res = await fetch(`/api/admin/stores/${selectedStoreId}/reconciliation-report?from=${fromDate}&to=${toDate}`, { headers: authHeaders() });
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? `Error ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reconciliation-${selectedStore.name.replace(/\s+/g, "-").toLowerCase()}-${fromDate}-to-${toDate}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      toast({ title: (e instanceof Error ? e.message : null) ?? "Failed to generate PDF", variant: "destructive" });
    } finally {
      setPdfDownloading(false);
    }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Reconciliation</h2>
        <p className="text-sm text-slate-500 mt-1">Live Stripe data and full fee reconciliation for any connected food store. Stripe fees and Ayrshire Hub/driver fees shown separately.</p>
      </div>

      {/* Store selector */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <label className="text-sm font-semibold text-slate-700 mb-2 block">Select Store</label>
        {storesLoading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-2"><LoaderCircle size={16} className="animate-spin" /> Loading stores…</div>
        ) : (
          <select
            className="w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-primary/30"
            value={selectedStoreId ?? ""}
            onChange={(e) => { setSelectedStoreId(e.target.value ? Number(e.target.value) : null); setReportData(null); }}
          >
            <option value="">— choose a store —</option>
            {connectedStores.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.ownerName ? ` (${s.ownerName})` : ""}</option>
            ))}
          </select>
        )}
        {!storesLoading && connectedStores.length === 0 && (
          <p className="text-xs text-slate-400 mt-2">No stores with Stripe connected yet.</p>
        )}
        {!storesLoading && stores.length > connectedStores.length && (
          <p className="text-xs text-amber-600 mt-2">{stores.length - connectedStores.length} store{stores.length - connectedStores.length !== 1 ? "s" : ""} without Stripe hidden.</p>
        )}
      </div>

      {selectedStore && (
        <>
          {/* Live balance */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-slate-800">{selectedStore.name}</h3>
                <p className="text-xs text-slate-400 font-mono">{selectedStore.stripeAccountId}</p>
                {selectedStore.ownerEmail && <p className="text-xs text-slate-400">{selectedStore.ownerEmail}</p>}
              </div>
              <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                <CheckCircle2 size={11} /> Stripe Connected
              </span>
            </div>

            {overviewLoading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-2"><LoaderCircle size={16} className="animate-spin" /> Loading balance…</div>
            ) : overview ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                    <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">Available</p>
                    <p className="text-xl font-bold text-emerald-700">{overview.balance.available.map((b) => pence(b.amount)).join(", ") || "£0.00"}</p>
                    <p className="text-xs text-emerald-500 mt-0.5">Ready to pay out</p>
                  </div>
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Pending</p>
                    <p className="text-xl font-bold text-amber-600">{overview.balance.pending.map((b) => pence(b.amount)).join(", ") || "£0.00"}</p>
                    <p className="text-xs text-amber-500 mt-0.5">In transit</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Next Payout</p>
                    {(() => {
                      const next = overview.payouts.find((p) => p.status === "in_transit" || p.status === "pending");
                      return next ? (
                        <>
                          <p className="text-xl font-bold text-slate-700">{pence(next.amount)}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{new Date(next.arrival_date * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
                        </>
                      ) : <p className="text-sm text-slate-400 mt-1">—</p>;
                    })()}
                  </div>
                </div>

                {/* Expandable recent payouts */}
                {overview.payouts.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Recent Payouts</p>
                      <p className="text-[11px] text-slate-400">{overview.payouts.length} total</p>
                    </div>
                    <p className="text-[11px] text-slate-400 mb-3">Click any row to see the full charge breakdown with Stripe and AH/driver fees.</p>
                    <div className="space-y-1.5">
                      {overview.payouts.slice(payoutPage * PAYOUT_PAGE_SIZE, (payoutPage + 1) * PAYOUT_PAGE_SIZE).map((p) => {
                        const isEx = expandedRecentPayoutId === p.id;
                        const detail = recentPayoutDetails.get(p.id);
                        const loading = recentPayoutLoading === p.id;
                        const arrDate = new Date(p.arrival_date * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                        const sc = p.status === "paid" ? "bg-emerald-100 text-emerald-700" : p.status === "in_transit" ? "bg-blue-100 text-blue-700" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
                        const sl = p.status === "in_transit" ? "In Transit" : p.status === "paid" ? "Paid" : p.status === "pending" ? "Scheduled" : p.status;
                        return (
                          <div key={p.id} className="border border-slate-100 rounded-xl overflow-hidden">
                            <button
                              className="w-full flex items-center gap-3 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                              onClick={() => {
                                if (isEx) { setExpandedRecentPayoutId(null); return; }
                                setExpandedRecentPayoutId(p.id);
                                void loadRecentPayoutDetail(p.id);
                              }}
                            >
                              <span className="text-xs text-slate-600 flex-1">{arrDate}{p.description ? <span className="text-slate-400 ml-1.5">· {p.description}</span> : null}</span>
                              <span className="text-sm font-bold font-mono text-slate-800">{pence(p.amount)}</span>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sc}`}>{sl}</span>
                              <span className="font-mono text-slate-300 text-[10px]">{p.id.slice(-10)}</span>
                              {loading ? <LoaderCircle size={13} className="animate-spin text-slate-400 flex-shrink-0" /> : isEx ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
                            </button>
                            {isEx && (
                              loading ? (
                                <div className="px-4 py-3 text-xs text-slate-400 flex items-center gap-2"><LoaderCircle size={13} className="animate-spin" /> Loading charge breakdown…</div>
                              ) : detail ? (
                                <div>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 py-3 bg-white border-b border-slate-100">
                                    {[
                                      { label: "Gross", val: detail.totalGrossPence, c: "text-slate-800" },
                                      { label: "Stripe Fee", val: detail.totalStripeProcessingFeePence, c: "text-red-500" },
                                      { label: "AH/Driver Fees", val: detail.totalApplicationFeePence, c: "text-red-500" },
                                      { label: "Net to Bank", val: detail.totalNetToStorePence, c: "text-emerald-600 font-bold" },
                                    ].map(({ label, val, c }) => (
                                      <div key={label} className="text-center">
                                        <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
                                        <p className={`text-xs font-mono mt-0.5 ${c}`}>{pence(val)}</p>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs min-w-[560px]">
                                      <thead>
                                        <tr className="bg-slate-700 text-white">
                                          <th className="text-left px-3 py-2 font-semibold">Date</th>
                                          <th className="text-left px-2 py-2 font-semibold">Customer</th>
                                          <th className="text-left px-2 py-2 font-semibold">Ref</th>
                                          <th className="text-right px-2 py-2 font-semibold">Gross</th>
                                          <th className="text-right px-2 py-2 font-semibold">Stripe Fee</th>
                                          <th className="text-right px-2 py-2 font-semibold">AH/Driver Fees</th>
                                          <th className="text-right px-2 py-2 font-semibold">Net to Store</th>
                                          <th className="text-right px-3 py-2 font-semibold">Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detail.charges.length === 0 ? (
                                          <tr><td colSpan={8} className="px-4 py-3 text-slate-400 text-center">No charges</td></tr>
                                        ) : detail.charges.map((c, ci) => {
                                          const dt = c.order ? new Date(c.order.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
                                          const osl = c.order?.status === "delivered" ? <span className="text-emerald-600 font-semibold">Delivered</span> : c.order?.status === "cancelled" ? <span className="text-red-500 font-semibold">Cancelled</span> : c.order ? <span className="text-blue-600 capitalize">{c.order.status}</span> : <span className="text-amber-600">No match</span>;
                                          const ahFees = c.applicationFeePence;
                                          return (
                                            <tr key={c.chargeId} className={`border-b border-slate-50 last:border-0 ${!c.order ? "bg-amber-50" : ci % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                                              <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{dt}</td>
                                              <td className="px-2 py-1.5 text-slate-700 max-w-[100px] truncate">{c.order?.customerName ?? "—"}</td>
                                              <td className="px-2 py-1.5 font-mono text-slate-300 text-[10px]">{(c.paymentIntentId ?? c.chargeId).slice(-10)}</td>
                                              <td className="px-2 py-1.5 text-right font-mono text-slate-700">{pence(c.grossPence)}</td>
                                              <td className="px-2 py-1.5 text-right font-mono text-red-400">−{pence(c.stripeProcessingFeePence)}</td>
                                              <td className="px-2 py-1.5 text-right font-mono text-red-400">{ahFees > 0 ? `−${pence(ahFees)}` : "—"}</td>
                                              <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800">{pence(c.netToStorePence)}</td>
                                              <td className="px-3 py-1.5 text-right">{osl}</td>
                                            </tr>
                                          );
                                        })}
                                        <tr className="bg-slate-100 border-t border-slate-200 font-bold text-xs">
                                          <td colSpan={3} className="px-3 py-2 text-right text-slate-500">Payout Total</td>
                                          <td className="px-2 py-2 text-right font-mono text-slate-800">{pence(detail.totalGrossPence)}</td>
                                          <td className="px-2 py-2 text-right font-mono text-red-400">−{pence(detail.totalStripeProcessingFeePence)}</td>
                                          <td className="px-2 py-2 text-right font-mono text-red-400">−{pence(detail.totalApplicationFeePence)}</td>
                                          <td className="px-2 py-2 text-right font-mono text-emerald-700">{pence(p.amount)}</td>
                                          <td></td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : null
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {overview.payouts.length > PAYOUT_PAGE_SIZE && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                        <button
                          onClick={() => setPayoutPage((p) => Math.max(0, p - 1))}
                          disabled={payoutPage === 0}
                          className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >← Newer</button>
                        <p className="text-[11px] text-slate-400">
                          {payoutPage * PAYOUT_PAGE_SIZE + 1}–{Math.min((payoutPage + 1) * PAYOUT_PAGE_SIZE, overview.payouts.length)} of {overview.payouts.length}
                        </p>
                        <button
                          onClick={() => setPayoutPage((p) => p + 1)}
                          disabled={(payoutPage + 1) * PAYOUT_PAGE_SIZE >= overview.payouts.length}
                          className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >Older →</button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* Period report */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
            <div>
              <h3 className="font-semibold text-slate-700 text-sm">Reconciliation Report</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">Full breakdown from Stripe payouts matched to DB orders. Stripe fee and AH/driver fees shown as separate totals.</p>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-500">From</label>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-primary/40" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-500">To</label>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-primary/40" />
              </div>
              <Button variant="outline" className="gap-2" onClick={() => void loadReport()} disabled={reportLoading || !fromDate || !toDate}>
                {reportLoading ? <><LoaderCircle size={14} className="animate-spin" /> Loading…</> : "Load Report"}
              </Button>
              <Button className="gap-2" onClick={() => void downloadPdf()} disabled={pdfDownloading || !fromDate || !toDate}>
                {pdfDownloading ? <><LoaderCircle size={14} className="animate-spin" /> Generating…</> : <><FileDown size={14} /> Download PDF</>}
              </Button>
            </div>

            {reportData && (
              <div className="space-y-5">
                {/* KPI boxes */}
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
                  {[
                    { label: "Gross Charged", value: reportData.grandTotalGrossPence, color: "text-slate-800", sub: "Payments in arrived payouts" },
                    { label: "Stripe Fee", value: reportData.grandTotalStripeProcessingFeePence, color: "text-red-500", sub: "Card processing" },
                    { label: "AH/Driver Fees", value: reportData.grandTotalApplicationFeePence, color: "text-red-500", sub: "Hub & driver costs" },
                    { label: "Net to Store Bank", value: reportData.grandTotalPayoutsPence, color: "text-emerald-600", sub: "Actual payout deposits" },
                    { label: "Food Revenue (DB)", value: reportData.grandPeriodRevenuePence, color: "text-slate-700", sub: "Orders placed in period" },
                  ].map(({ label, value, color, sub }) => (
                    <div key={label} className="bg-slate-50 rounded-xl border border-slate-100 p-2.5 text-center">
                      <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1 leading-tight">{label}</p>
                      <p className={`text-sm font-bold font-mono ${color}`}>{pence(value)}</p>
                      <p className="text-[9px] text-slate-400 mt-0.5">{sub}</p>
                    </div>
                  ))}
                </div>

                {/* Payouts */}
                {reportData.payouts.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No payouts found for this period.</p>
                ) : (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Payout Breakdown ({reportData.payouts.length} payouts)</p>
                    <div className="space-y-2">
                      {reportData.payouts.map((p, pi) => {
                        const isExpanded = expandedPayoutId === p.id;
                        const sc = p.status === "paid" ? "bg-emerald-100 text-emerald-700" : p.status === "in_transit" ? "bg-blue-100 text-blue-700" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
                        const sl = p.status === "in_transit" ? "In Transit" : p.status === "paid" ? "Paid" : p.status;
                        return (
                          <div key={p.id} className="border border-slate-100 rounded-xl overflow-hidden">
                            <button
                              className="w-full flex items-center gap-2 px-3 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                              onClick={() => setExpandedPayoutId(isExpanded ? null : p.id)}
                            >
                              <span className="text-xs font-bold text-slate-400 w-5 flex-shrink-0">#{pi + 1}</span>
                              <span className="text-xs text-slate-600 flex-1 min-w-0">{new Date(p.arrivalDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}{p.description ? <span className="text-slate-400"> · {p.description}</span> : null}</span>
                              <span className="text-[10px] text-slate-400 flex-shrink-0">{p.charges.length}ch</span>
                              <span className="text-[10px] font-mono text-slate-500 flex-shrink-0 hidden sm:block">Gross {pence(p.totalGrossPence)}</span>
                              <span className="text-[10px] font-mono text-red-400 flex-shrink-0">Stripe −{pence(p.totalStripeProcessingFeePence)}</span>
                              {p.totalApplicationFeePence > 0 && <span className="text-[10px] font-mono text-red-400 flex-shrink-0 hidden md:block">AH −{pence(p.totalApplicationFeePence)}</span>}
                              <span className="text-sm font-bold font-mono text-slate-800 flex-shrink-0">{pence(p.amountPence)}</span>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${sc}`}>{sl}</span>
                              {p.unmatchedCount > 0 && <span className="text-[10px] font-semibold text-amber-600 flex-shrink-0">⚠{p.unmatchedCount}</span>}
                              {isExpanded ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
                            </button>

                            {isExpanded && (
                              <div>
                                <p className="px-4 py-3 text-xs text-amber-900">Provider member net: {p.bridge?.memberNetPence==null?'Unverified':pence(p.bridge.memberNetPence)} · Unmatched difference: {p.bridge?.differencePence==null?'Unverified':pence(p.bridge.differencePence)}. {p.bridge?.state==='matched'?'Provider members balance exactly.':'Review provider members.'} Bank arrival remains unverified.</p>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 py-2.5 bg-white border-b border-slate-100">
                                  {[
                                    { label: "Gross", val: p.totalGrossPence, c: "text-slate-800" },
                                    { label: "Stripe Fee", val: p.totalStripeProcessingFeePence, c: "text-red-500" },
                                    { label: "AH/Driver Fees", val: p.totalApplicationFeePence, c: "text-red-500" },
                                    { label: "Net to Bank", val: p.amountPence, c: "text-emerald-600 font-bold" },
                                  ].map(({ label, val, c }) => (
                                    <div key={label} className="text-center">
                                      <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
                                      <p className={`text-xs font-mono mt-0.5 ${c}`}>{pence(val)}</p>
                                    </div>
                                  ))}
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs min-w-[560px]">
                                    <thead>
                                      <tr className="bg-slate-700 text-white text-[10px]">
                                        <th className="text-left px-3 py-2 font-semibold">Date &amp; Time</th>
                                        <th className="text-left px-2 py-2 font-semibold">Customer</th>
                                        <th className="text-left px-2 py-2 font-semibold">Ref</th>
                                        <th className="text-right px-2 py-2 font-semibold">Gross</th>
                                        <th className="text-right px-2 py-2 font-semibold">Stripe Fee</th>
                                        <th className="text-right px-2 py-2 font-semibold">AH/Driver Fees</th>
                                        <th className="text-right px-2 py-2 font-semibold">Net to Store</th>
                                        <th className="text-right px-3 py-2 font-semibold">Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {p.charges.length === 0 ? (
                                        <tr><td colSpan={8} className="px-4 py-3 text-slate-400 text-center">No charges</td></tr>
                                      ) : p.charges.map((c, ci) => {
                                        const dt = c.order ? new Date(c.order.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
                                        const osl = c.order?.status === "delivered" ? <span className="text-emerald-600 font-semibold">Delivered</span> : c.order?.status === "cancelled" ? <span className="text-red-500 font-semibold">Cancelled</span> : c.order ? <span className="text-blue-600 capitalize">{c.order.status}</span> : <span className="text-amber-600">No match</span>;
                                        const ahFees = c.applicationFeePence;
                                        return (
                                          <tr key={c.chargeId} className={`border-b border-slate-50 last:border-0 ${!c.order ? "bg-amber-50" : ci % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                                            <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{dt}</td>
                                            <td className="px-2 py-1.5 text-slate-700 max-w-[100px] truncate">{c.order?.customerName ?? "—"}</td>
                                            <td className="px-2 py-1.5 font-mono text-slate-300 text-[10px]">{(c.paymentIntentId ?? c.chargeId).slice(-10)}</td>
                                            <td className="px-2 py-1.5 text-right font-mono text-slate-700">{pence(c.grossPence)}</td>
                                            <td className="px-2 py-1.5 text-right font-mono text-red-400">−{pence(c.stripeProcessingFeePence)}</td>
                                            <td className="px-2 py-1.5 text-right font-mono text-red-400">{ahFees > 0 ? `−${pence(ahFees)}` : "—"}</td>
                                            <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800">{pence(c.netToStorePence)}</td>
                                            <td className="px-3 py-1.5 text-right">{osl}</td>
                                          </tr>
                                        );
                                      })}
                                      <tr className="bg-slate-100 border-t border-slate-200 font-bold text-xs">
                                        <td colSpan={3} className="px-3 py-2 text-right text-slate-500">Payout Total</td>
                                        <td className="px-2 py-2 text-right font-mono text-slate-800">{pence(p.totalGrossPence)}</td>
                                        <td className="px-2 py-2 text-right font-mono text-red-400">−{pence(p.totalStripeProcessingFeePence)}</td>
                                        <td className="px-2 py-2 text-right font-mono text-red-400">−{pence(p.totalApplicationFeePence)}</td>
                                        <td className="px-2 py-2 text-right font-mono text-emerald-700">{pence(p.amountPence)}</td>
                                        <td></td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Order statement */}
                {reportData.weeks.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Order Statement</p>
                    <p className="text-[11px] text-slate-400 mb-2">Orders by week. Food Revenue = subtotal + bag. AH/Driver Fees = delivery, driver, and hub charges collected on behalf of this store.</p>
                    <div className="border border-slate-100 rounded-xl overflow-hidden">
                      <table className="w-full text-xs min-w-[560px]">
                        <thead>
                          <tr className="bg-slate-800 text-white text-[10px]">
                            <th className="text-left px-3 py-2 font-semibold">Date &amp; Time</th>
                            <th className="text-left px-2 py-2 font-semibold">Customer</th>
                            <th className="text-right px-2 py-2 font-semibold">Subtotal</th>
                            <th className="text-right px-2 py-2 font-semibold">Bag</th>
                            <th className="text-right px-2 py-2 font-semibold">AH/Driver Fees</th>
                            <th className="text-right px-2 py-2 font-semibold">Gross</th>
                            <th className="text-right px-2 py-2 font-semibold text-emerald-300">Food Revenue</th>
                            <th className="text-right px-3 py-2 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.weeks.map((w) => (
                            <>
                              <tr key={`wk-${w.periodStart}`} className="bg-slate-700">
                                <td colSpan={8} className="px-3 py-2 text-white font-semibold text-xs">
                                  {new Date(w.periodStart + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {new Date(w.periodEnd + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                  <span className="text-slate-300 ml-2 font-normal">· {w.orders.length} order{w.orders.length !== 1 ? "s" : ""} · Food revenue: {pence(w.totalFoodRevenuePence)}</span>
                                </td>
                              </tr>
                              {w.orders.length === 0 ? (
                                <tr><td colSpan={8} className="px-3 py-2 text-slate-400 text-center">No orders</td></tr>
                              ) : w.orders.map((o, oi) => {
                                const food = o.subtotalPence + (o.bagChargePence ?? 0);
                                const ahFees = o.deliveryFeePence + (o.leg2DeliveryFeePence ?? 0) + (o.customerPlatformFeePence ?? 0) + (o.customerHubDriverFeePence ?? 0) + (o.driverFuelSupplementPence ?? 0);
                                const gross = food + ahFees;
                                const osl = o.status === "delivered" ? <span className="text-emerald-600 font-semibold">Delivered</span> : o.status === "cancelled" ? <span className="text-red-500 font-semibold">Cancelled</span> : <span className="text-blue-600 capitalize font-semibold">{o.status}</span>;
                                return (
                                  <tr key={o.id} className={`border-b border-slate-50 last:border-0 ${oi % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                                    <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{new Date(o.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                                    <td className="px-2 py-1.5 text-slate-700 max-w-[120px] truncate">{o.customerName ?? "Unknown"}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-slate-700">{pence(o.subtotalPence)}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{o.bagChargePence ? pence(o.bagChargePence) : "—"}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{ahFees > 0 ? pence(ahFees) : "—"}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-slate-600">{pence(gross)}</td>
                                    <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800">{pence(food)}</td>
                                    <td className="px-3 py-1.5 text-right">{osl}</td>
                                  </tr>
                                );
                              })}
                              <tr className="bg-slate-100 border-t border-slate-200 font-bold text-xs">
                                <td colSpan={2} className="px-3 py-2 text-right text-slate-500">Week Total</td>
                                <td className="px-2 py-2 text-right font-mono text-slate-700">{pence(w.orders.reduce((s, o) => s + o.subtotalPence, 0))}</td>
                                <td className="px-2 py-2 text-right font-mono text-slate-500">{pence(w.orders.reduce((s, o) => s + (o.bagChargePence ?? 0), 0))}</td>
                                <td className="px-2 py-2 text-right font-mono text-slate-500">{pence(w.totalDeliveryFeePence + w.totalPlatformFeePence + w.orders.reduce((s, o) => s + (o.customerHubDriverFeePence ?? 0) + (o.driverFuelSupplementPence ?? 0), 0))}</td>
                                <td className="px-2 py-2 text-right font-mono text-slate-600"></td>
                                <td className="px-2 py-2 text-right font-mono text-emerald-700">{pence(w.totalFoodRevenuePence)}</td>
                                <td></td>
                              </tr>
                            </>
                          ))}
                          <tr className="bg-slate-200 font-bold text-xs border-t-2 border-slate-300">
                            <td colSpan={2} className="px-3 py-2 text-right text-slate-700">Period Total</td>
                            <td className="px-2 py-2 text-right font-mono text-slate-700">{pence(reportData.weeks.reduce((s, w) => s + w.orders.reduce((ss, o) => ss + o.subtotalPence, 0), 0))}</td>
                            <td className="px-2 py-2 text-right font-mono text-slate-500">{pence(reportData.weeks.reduce((s, w) => s + w.orders.reduce((ss, o) => ss + (o.bagChargePence ?? 0), 0), 0))}</td>
                            <td className="px-2 py-2 text-right font-mono text-slate-500">{pence(reportData.grandTotalDeliveryFeePence + reportData.grandTotalPlatformFeePence + reportData.grandTotalHubDriverFeePence + reportData.grandTotalFuelSupplementPence)}</td>
                            <td className="px-2 py-2 text-right font-mono text-slate-600"></td>
                            <td className="px-2 py-2 text-right font-mono text-emerald-700 text-sm">{pence(reportData.grandPeriodRevenuePence)}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
